import {FC, useState, useEffect} from 'react'
import {Card, Row, Col, Spinner, Button, Form} from 'react-bootstrap'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {useAbility} from '@casl/react'
import {PrefTariffModel} from '../../tariff/core/_models'
import {AbilityContext} from '../../../../modules/auth/core/AbilityContext'
import useAxiosPrivate from '../../../../libs/useAxiosPrivate'
import {
  createTariffByType,
  getTariffByTypeAndID,
  updateTariffByTypeAndID,
  getHSDetail,
} from '../core/_requests'
import {PageTitle} from '../../../../../_metronic/layout/core'
import EconDropdown from '../../../../../ui-kit/EconDropdown'
import HSDropdown from './HSDropdown'
import HSDropdownWithLoadMore from './HSDropdownWithLoadMore'
import HSDescriptionDetailView from './HSDescriptionDetailView'
import PrefTariffCodeDropdown from './PrefTariffCodeDropdown'
import PrefTariffSourceAutocomplete from './PrefTariffSourceAutocomplete'
import {Toast, showSwal} from '../../../../../ui-kit'

const confirmDialog = (title: string) =>
  showSwal({
    title,
    icon: 'question',
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
  })

const INITIAL_STATE: PrefTariffModel = {
  rep: '',
  code: '',
  hs: '',
  year_org: 0,
  isAVE: 0,
  hs_ver_org: '',
  src: '',
  year: 0,
  val: 0,
}

export type PrefTariffFormProps = {
  subject: string
  type: string
  HSCodes: any[]
  HSVersions: any[]
}

const PrefTariffForm: FC<PrefTariffFormProps> = ({subject, type, HSCodes, HSVersions}) => {
  const ability = useAbility(AbilityContext)
  const navigate = useNavigate()
  const axiosPrivate = useAxiosPrivate()
  const {idTariff} = useParams()

  const isDetailMode = Boolean(idTariff)
  const isAddMode = !idTariff

  const [isEditMode, setIsEditMode] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [hsDescriptionData, setHSDescriptionData] = useState<any>(null)
  const [isFormValid, setIsFormValid] = useState(false)
  const [formData, setFormData] = useState<PrefTariffModel>({
    ...INITIAL_STATE,
  })

  const isEditable = isEditMode || isAddMode

  const validateForm = () => {
    const requiredFields: (keyof PrefTariffModel)[] = [
      'rep',
      'code',
      'hs',
      'hs_ver_org',
      'year',
      'val',
    ]
    const isValid = requiredFields.every((key) => {
      const val = formData[key]
      return val !== undefined && val !== null && String(val).trim() !== ''
    })
    setIsFormValid(isValid)
  }

  useEffect(() => {
    validateForm()
  }, [formData])

  useEffect(() => {
    if (!isDetailMode) return
    const fetch = async () => {
      try {
        setIsLoadingData(true)
        const res = await getTariffByTypeAndID(axiosPrivate, type, idTariff)
        if (res?.data?.data) {
          const data = res.data.data
          setFormData(data)

          // Fetch detailed HS description
          try {
            const resDetail = await getHSDetail(axiosPrivate, data.hs, data.hs_ver_org)
            if (resDetail?.data?.data) {
              setHSDescriptionData(resDetail.data.data)
            }
          } catch (err) {
            console.error('Failed to fetch detailed HS description', err)
            Toast(
              'Error when fetching tariff data',
              'danger',
              <Link to={`/master-data-hs-descriptions/add?ver=${data.hs_ver_org}&code=${data.hs}`}>
                Create new HS Description
              </Link>
            )
          }
        }
      } catch (err) {
        console.error(err)
      } finally {
        setIsLoadingData(false)
      }
    }
    fetch()
  }, [idTariff])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target
    setFormData((prev) => ({...prev, [name]: value}))
  }

  const handleSubmit = () => {
    confirmDialog(
      `Are you sure you want to ${isEditMode ? 'update' : 'create'} this Preferential Tariff?`
    ).then((result) => {
      if (!result.isConfirmed) return
      setIsLoadingButton(true)

      const action = isEditMode
        ? updateTariffByTypeAndID(axiosPrivate, type, formData)
        : createTariffByType(axiosPrivate, type, formData)

      const successStatus = isEditMode ? 200 : 201
      const successMessage = isEditMode ? 'Data updated' : 'Data created'
      const errorMessage = isEditMode
        ? 'Failed to update Preferential Tariff'
        : 'Failed to create Preferential Tariff'

      action
        .then((res) => {
          if (res.status === successStatus) {
            showSwal({title: successMessage, icon: 'success', confirmButtonText: 'OK'}).then(
              () => navigate('/tariff/pref')
            )
          }
        })
        .catch((err) => {
          console.error(err)
          const message = err?.response?.data?.message || errorMessage
          showSwal('Error', message, 'error')
        })
        .finally(() => setIsLoadingButton(false))
    })
  }

  const handleCancel = () => {
    confirmDialog('Are you sure you want to cancel?').then((result) => {
      if (!result.isConfirmed) return
      if (isEditMode) {
        window.location.reload()
      } else {
        navigate('/tariff/pref')
      }
    })
  }

  const ReadOnlyField = ({value}: {value: string | number | undefined}) => (
    <div className='data-value'>{value ?? '-'}</div>
  )

  const fieldLabel = (label: string, required = false) => (
    <Form.Label
      className={`text-primary me-0 ps-0 ${required && isEditable ? 'required' : ''}`}
      column
    >
      {label}
    </Form.Label>
  )

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Home', path: '/', isSeparator: false, isActive: false},
          {title: '', path: '', isSeparator: true, isActive: false},
          {
            title: 'Tariff',
            path: '/tariff',
            isSeparator: false,
            isActive: false,
          },
          {title: '', path: '', isSeparator: true, isActive: false},
          {
            title: 'Preferential Tariff',
            path: '/tariff/pref',
            isSeparator: false,
            isActive: false,
          },
          {title: '', path: '', isSeparator: true, isActive: false},
        ]}
      >
        {isDetailMode ? 'Detail' : 'Add'}
      </PageTitle>

      <div className='MasterDataPage'>
        <Row>
          <Col>
            <Card className='mb-5'>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-file-alt px-2'></span>
                      <h3 className='text-gray-700'>
                        {isDetailMode ? `Detail Preferential Tariff` : 'Add Preferential Tariff'}
                      </h3>
                    </div>
                    {!isEditMode && !isAddMode && ability.can('update', subject) && (
                      <Button
                        className='btn btn-primary d-flex gap-4 align-items-center'
                        onClick={() => setIsEditMode(true)}
                      >
                        <span className='fa fa-pencil'></span>
                        Edit
                      </Button>
                    )}
                  </div>
                </Card.Title>
              </Card.Header>

              <Card.Body>
                {isLoadingData ? (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{height: '50vh'}}
                  >
                    <Spinner animation='border' role='status' variant='primary' className='me-3' />
                    <div className='loading-text'>
                      Loading<span className='dot'>.</span>
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <Form>
                      <div className='row'>
                        {/* REP */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('Reporter', true)}
                          {isEditable ? (
                            <EconDropdown
                              econ={formData.rep}
                              onSelect={(value) =>
                                setFormData({
                                  ...formData,
                                  rep: value,
                                })
                              }
                            />
                          ) : (
                            <ReadOnlyField value={formData.rep} />
                          )}
                        </div>

                        {/* HS Ver Org */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('HS Version', true)}
                          {isEditable ? (
                            <HSDropdown
                              items={HSVersions}
                              value={formData.hs_ver_org}
                              placeholder='Select HS Version'
                              onSelect={(value) =>
                                setFormData({
                                  ...formData,
                                  hs_ver_org: value,
                                })
                              }
                            />
                          ) : (
                            <ReadOnlyField value={formData.hs_ver_org} />
                          )}
                        </div>

                        {/* HS */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('HS Code', true)}
                          {isEditable ? (
                            <HSDropdownWithLoadMore
                              value={formData.hs}
                              placeholder='Select HS Code'
                              onSelect={(value) =>
                                setFormData({
                                  ...formData,
                                  hs: value,
                                })
                              }
                            />
                          ) : (
                            <ReadOnlyField value={formData.hs} />
                          )}
                        </div>

                        {/* Code */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('Code', true)}
                          {isEditable ? (
                            <PrefTariffCodeDropdown
                              value={formData.code}
                              onSelect={(value) =>
                                setFormData({
                                  ...formData,
                                  code: value,
                                })
                              }
                            />
                          ) : (
                            <ReadOnlyField value={formData.code} />
                          )}
                        </div>

                        {/* Year */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('Year', true)}
                          {isEditable ? (
                            <Form.Control
                              placeholder='Enter year'
                              type='number'
                              name='year'
                              min={0}
                              value={formData.year}
                              onChange={handleChange}
                            />
                          ) : (
                            <ReadOnlyField value={formData.year} />
                          )}
                        </div>

                        {/* Val */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('Value', true)}
                          {isEditable ? (
                            <Form.Control
                              placeholder='Enter value'
                              type='number'
                              step='0.01'
                              min={0}
                              name='val'
                              value={formData.val}
                              onChange={handleChange}
                            />
                          ) : (
                            <ReadOnlyField value={formData.val} />
                          )}
                        </div>

                        {/* Year Org */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('Original Year')}
                          {isEditable ? (
                            <Form.Control
                              placeholder='Enter original year'
                              type='number'
                              min={0}
                              name='year_org'
                              value={formData.year_org}
                              onChange={handleChange}
                            />
                          ) : (
                            <ReadOnlyField value={formData.year_org} />
                          )}
                        </div>

                        {/* isAVE */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('Is AVE')}
                          {isEditable ? (
                            <div className='d-flex gap-3 align-items-center' style={{minHeight: '38px'}}>
                              <Form.Check
                                type='radio'
                                label='Yes'
                                value={1}
                                onChange={handleChange}
                                checked={formData.isAVE == 1}
                                name='isAVE'
                                id='radio-ave-1'
                              />
                              <Form.Check
                                type='radio'
                                label='No'
                                value={0}
                                onChange={handleChange}
                                checked={formData.isAVE == 0}
                                name='isAVE'
                                id='radio-ave-2'
                              />
                            </div>
                          ) : (
                            <ReadOnlyField value={formData.isAVE ? 'Yes' : 'No'} />
                          )}
                        </div>

                        {/* Src */}
                        <div className='col-md-4 mb-5'>
                          {fieldLabel('Source')}
                          {isEditable ? (
                            <PrefTariffSourceAutocomplete
                              placeholder='Enter source'
                              value={formData.src}
                              onChange={(value) =>
                                setFormData({
                                  ...formData,
                                  src: value,
                                })
                              }
                            />
                          ) : (
                            <ReadOnlyField value={formData.src} />
                          )}
                        </div>
                      </div>

                      {isEditable && (
                        <div className='row'>
                          <div className='col-lg-12 d-flex justify-content-end gap-3'>
                            <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                              Cancel
                            </Button>
                            <Button
                              variant='primary'
                              onClick={handleSubmit}
                              disabled={isLoadingButton || !isFormValid}
                            >
                              {isLoadingButton ? (
                                <>
                                  <Spinner animation='border' size='sm' className='me-2' />
                                  Submit
                                </>
                              ) : (
                                'Submit'
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                    </Form>

                    {isDetailMode && (
                      <>
                        <hr className='my-10' />
                        <HSDescriptionDetailView hsDescriptionData={hsDescriptionData} />
                      </>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default PrefTariffForm
